// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Store, ArrowUpRight } from 'lucide-react';
import { getMarketplaceMatches, MarketplaceMatch } from '../services/api';

/**
 * Marketplace match card for the consultant's client detail page — the match
 * landing OUTSIDE the Orbit chat stream. When the matcher finds existing
 * solutions that fit this client's dossier, they surface here so the
 * consultant sees the product path without opening a scoping session.
 *
 * States: loading → skeleton; no matches / error → renders nothing (this is
 * a bonus surface, never clutter); matches → up to three rows, each with the
 * solution name, tagline, a one-sentence fit reason, and a link to the
 * solution's landing page.
 */

/** One consultant-voiced sentence from the matcher's reasons. The matcher
 * speaks to the client ("your problem mentions ..."); this surface speaks
 * to the consultant about the client, so reword to the dossier's voice. */
const fitSentence = (reasons: string[]): string => {
  if (!reasons || reasons.length === 0) return 'Strong shape match for this dossier.';
  const punchiest = [...reasons].sort((a, b) => {
    const ap = a.toLowerCase().includes('your problem') ? 0 : 1;
    const bp = b.toLowerCase().includes('your problem') ? 0 : 1;
    return ap - bp;
  })[0];
  const reworded = punchiest
    .replace(/^your problem mentions/i, 'The dossier mentions')
    .replace(/^matches problem category:/i, 'Matches the problem category:')
    .replace(/^matches help preference:/i, 'Matches the help preference:')
    .replace(/^fits org type:/i, 'Fits the org type:')
    .replace(/_/g, ' ');
  const sentence = reworded.charAt(0).toUpperCase() + reworded.slice(1);
  return sentence.endsWith('.') ? sentence : `${sentence}.`;
};

const MarketplaceMatchCard: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [matches, setMatches] = useState<MarketplaceMatch[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getMarketplaceMatches(clientId)
      .then((res) => {
        if (!cancelled) setMatches(res.matches || []);
      })
      .catch((err) => {
        // No toast — an unavailable matcher must not degrade the page.
        console.error('[MarketplaceMatchCard] match load failed', err);
        if (!cancelled) setMatches([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (loading) {
    return (
      <div className="bg-white rounded-card border border-surface-border shadow-card mb-6 animate-pulse">
        <div className="px-5 py-4 border-b border-surface-border">
          <div className="h-4 w-40 bg-gray-100 rounded" />
        </div>
        <div className="p-5 space-y-3">
          <div className="h-4 w-3/5 bg-gray-100 rounded" />
          <div className="h-3 w-4/5 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (!matches || matches.length === 0) return null;

  return (
    <div className="bg-white rounded-card border border-surface-border shadow-card mb-6">
      <div className="px-5 py-4 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <Store size={16} className="text-brand-500" />
          <h2 className="text-sm font-semibold text-gray-900">Marketplace matches</h2>
        </div>
        <p className="text-xs text-gray-400 mt-0.5">
          Existing solutions that fit this client's dossier.
        </p>
      </div>
      <div className="divide-y divide-surface-border">
        {matches.slice(0, 3).map((match) => {
          const solution = match.solution || {};
          return (
            <div key={solution.slug || solution.id} className="p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{solution.name}</div>
                {solution.tagline && (
                  <div className="text-xs text-gray-500 mt-0.5">{solution.tagline}</div>
                )}
                <div className="text-xs text-gray-400 mt-1.5">{fitSentence(match.reasons)}</div>
              </div>
              {solution.landing_url && (
                <a
                  href={solution.landing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 border border-surface-border rounded-lg hover:bg-gray-50 transition-colors shrink-0 whitespace-nowrap"
                >
                  View solution
                  <ArrowUpRight size={12} />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MarketplaceMatchCard;
